import React, { useMemo, useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { DevContext } from '../../../../../App';
import { getLocalizedText } from '../../../../../utils/localizedText';

export default function InformationStep({ onComplete, stepId }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange } = useFormData();
  const { goToNextStep } = useStepNavigation();
  const { eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get current language from i18n (not from form data)
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Get information content from event configuration
  const informationContent = useMemo(() => {
    if (!eventConfig?.informationContent) {
      return null;
    }
    
    // Find content directly by current language
    const matchingContent = eventConfig.informationContent.find(content => content.language === currentLanguage);
    
    // If no exact match, try language prefix (e.g., 'en' for 'en-US')
    const languagePrefix = currentLanguage.split('-')[0];
    const prefixMatch = eventConfig.informationContent.find(content => content.language === languagePrefix);
    
    // Fallback priority: exact match > prefix match > English > first available
    return matchingContent || prefixMatch || 
           eventConfig.informationContent.find(content => content.language === 'en') ||
           eventConfig.informationContent[0];
  }, [eventConfig, currentLanguage]);

  const handleContinue = () => {
    // Mark that user has read the information
    handleFieldChange('hasReadInformation', true);
    onComplete?.(stepId);
    goToNextStep();
  };

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
        <MDBTypography tag="h3" className="mb-4">
          {t('Information')}
        </MDBTypography>
        
        {!informationContent && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-warning">
                <MDBCardBody>
                  <MDBTypography tag="p" className="mb-0 text-warning">
                    {t('No information content configured for this event.')}
                  </MDBTypography>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        )}
        
        {informationContent && (
          <MDBRow className="mb-4">
            <MDBCol>
              <div 
                className="information-content"
                dangerouslySetInnerHTML={{ __html: informationContent.content }}
                style={{
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}
              />
            </MDBCol>
          </MDBRow>
        )}

        {/* Debug Info */}
        {isDevMode && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-info">
                <MDBCardBody>
                  <MDBTypography tag="h6" className="mb-3">
                    🔍 Debug Info (Dev Mode) - Information Step
                  </MDBTypography>
                
                  <div className="mb-2">
                    <strong>Current Language:</strong> {currentLanguage}
                  </div>
                  
                  <div className="mb-2">
                    <strong>Available Languages:</strong>{' '}
                    {eventConfig?.informationContent?.map(content => content.language).join(', ') || 'none'}
                  </div>
                  
                  <div className="mb-2">
                    <strong>Content Available:</strong>{' '}
                    <MDBBadge color={informationContent ? 'success' : 'warning'}>
                      {informationContent ? 'Yes' : 'No'}
                    </MDBBadge>
                  </div>
                  
                  {informationContent && (
                    <div className="mb-2">
                      <strong>Content Language:</strong> {informationContent.language}
                    </div>
                  )}
                  
                  <div className="mb-2">
                    <strong>Step Requirements:</strong> No validation required (information only)
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        )}
      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center">
              <MDBBtn onClick={handleContinue}>
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}